import { FC, useState } from 'react'
import classNames from 'classnames/bind'
import { UserSession, UserSessionDevice } from '../../../../../model/UserSession'
import { AccordionItem, Icon } from '../../../../shared'
import { getV3ColorCssVariable } from '../../../../../design-system/colors_v3'
import { getDeviceIcon, getDeviceKindLabel } from '../../../../../misc/deviceIcon.utilities'
import { getDistanceInWordsToNow, toDate } from '../../../../../misc/date.utilities'
import {
  getDeviceIpAddress,
  getDeviceLabel,
  getSessionClients,
  isDeviceCurrent,
} from '../../../../../misc/userSession.utilities'
import styles from './DeviceCard.module.scss'

const cx = classNames.bind(styles)

const formatLastActivity = (value: string | number | undefined): string => {
  const date = toDate(value)
  return date ? getDistanceInWordsToNow(date, { addSuffix: true }) : ''
}

export interface DeviceCardProps {
  device: UserSessionDevice
  onRequestLogoutSession: (session: UserSession, device: UserSessionDevice) => void
  actionsDisabled?: boolean
}

const CurrentSessionBadge: FC = () => <span className={cx('currentBadge')}>Session actuelle</span>

export const DeviceCard: FC<DeviceCardProps> = ({
  device,
  onRequestLogoutSession,
  actionsDisabled = false,
}) => {
  const [expanded, setExpanded] = useState(false)

  const isCurrent = isDeviceCurrent(device)
  const label = getDeviceLabel(device)
  const icon = getDeviceIcon(device.mobile, device.device)
  const kindLabel = getDeviceKindLabel(device.mobile, device.device)
  const sessionCount = device.sessions.length

  const subtextParts = [
    device.osVersion?.trim(),
    getDeviceIpAddress(device),
    `Dernière activité : ${formatLastActivity(device.lastAccess)}`,
  ].filter(Boolean)

  return (
    <div className={cx('card')}>
      <div className={cx('body')}>
        <div className={cx('iconWrapper')} aria-hidden>
          <Icon icon={icon} size="normal" color={getV3ColorCssVariable('shades', 'shade2')} />
        </div>
        <div className={cx('info')}>
          <div className={cx('titleRow')}>
            <span className={cx('title')}>{label}</span>
            <span className={cx('srOnly')}>{kindLabel}</span>
            {isCurrent && <CurrentSessionBadge />}
          </div>
          <p className={cx('subtext')}>{subtextParts.join(' · ')}</p>
        </div>
      </div>

      <div className={cx('accordionRoot')} data-test-id="accordion-device_card-sessions-root">
        <AccordionItem
          open={expanded}
          onOpenClose={() => setExpanded((current) => !current)}
          renderLabel={() => (
            <span className={cx('accordionLabel')}>
              {sessionCount} session{sessionCount > 1 ? 's' : ''}
            </span>
          )}
          testId="accordion-device_card-sessions"
        >
          <ul className={cx('sessionList')}>
            {device.sessions.map((session) => {
              const clients = getSessionClients(session)
              const clientsLabel = clients.length > 0 ? clients.join(', ') : 'Session active'

              return (
                <li key={session.id} className={cx('sessionItem')}>
                  <div className={cx('sessionInfo')}>
                    <p className={cx('sessionClients')}>{clientsLabel}</p>
                    <p className={cx('sessionActivity')}>
                      Dernière activité : {formatLastActivity(session.lastAccess)}
                    </p>
                  </div>
                  {session.current ? (
                    <div className={cx('sessionActions')}>
                      <CurrentSessionBadge />
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={cx('sessionLogoutButton')}
                      disabled={actionsDisabled}
                      onClick={() => onRequestLogoutSession(session, device)}
                      data-test-id="button-device_card-logout_session"
                    >
                      Déconnecter
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </AccordionItem>
      </div>
    </div>
  )
}
